import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function StudyDetails() {
  // Actual Doherty Threshold lookup table data
  const dohertyData = [
    { ept: 0.4, transactions: 14.70, theoreticalMax: 42.86 },
    { ept: 0.5, transactions: 11.76, theoreticalMax: 40.00 },
    { ept: 0.6, transactions: 9.80, theoreticalMax: 37.50 },
    { ept: 0.7, transactions: 8.40, theoreticalMax: 35.29 },
    { ept: 0.8, transactions: 7.35, theoreticalMax: 33.33 },
    { ept: 0.9, transactions: 6.53, theoreticalMax: 31.58 },
    { ept: 1.0, transactions: 5.88, theoreticalMax: 30.00 },
    { ept: 1.1, transactions: 5.35, theoreticalMax: 28.57 },
    { ept: 1.2, transactions: 4.90, theoreticalMax: 27.27 },
    { ept: 1.3, transactions: 4.52, theoreticalMax: 26.09 },
    { ept: 1.4, transactions: 4.20, theoreticalMax: 25.00 },
    { ept: 1.5, transactions: 3.92, theoreticalMax: 24.00 },
    { ept: 1.6, transactions: 3.68, theoreticalMax: 23.08 },
    { ept: 1.7, transactions: 3.46, theoreticalMax: 22.22 },
    { ept: 1.8, transactions: 3.27, theoreticalMax: 21.43 },
    { ept: 1.9, transactions: 3.09, theoreticalMax: 20.69 },
    { ept: 2.0, transactions: 2.94, theoreticalMax: 20.00 },
    { ept: 2.1, transactions: 2.80, theoreticalMax: 19.35 },
    { ept: 2.2, transactions: 2.67, theoreticalMax: 18.75 },
    { ept: 2.3, transactions: 2.56, theoreticalMax: 18.18 },
    { ept: 2.4, transactions: 2.45, theoreticalMax: 17.65 },
    { ept: 2.5, transactions: 2.35, theoreticalMax: 17.14 },
    { ept: 2.6, transactions: 2.26, theoreticalMax: 16.67 },
    { ept: 2.7, transactions: 2.18, theoreticalMax: 16.22 },
    { ept: 2.8, transactions: 2.10, theoreticalMax: 15.79 },
    { ept: 2.9, transactions: 2.03, theoreticalMax: 15.38 },
    { ept: 3.0, transactions: 1.96, theoreticalMax: 15.00 },
    { ept: 3.1, transactions: 1.90, theoreticalMax: 14.63 },
    { ept: 3.2, transactions: 1.84, theoreticalMax: 14.29 },
    { ept: 3.3, transactions: 1.78, theoreticalMax: 13.95 },
    { ept: 3.4, transactions: 1.73, theoreticalMax: 13.64 },
    { ept: 3.5, transactions: 1.68, theoreticalMax: 13.33 },
    { ept: 3.6, transactions: 1.63, theoreticalMax: 13.04 },
    { ept: 3.7, transactions: 1.59, theoreticalMax: 12.77 },
    { ept: 3.8, transactions: 1.55, theoreticalMax: 12.50 },
    { ept: 3.9, transactions: 1.51, theoreticalMax: 12.24 },
    { ept: 4.0, transactions: 1.47, theoreticalMax: 12.00 },
    { ept: 4.1, transactions: 1.43, theoreticalMax: 11.76 },
    { ept: 4.2, transactions: 1.40, theoreticalMax: 11.54 },
    { ept: 4.3, transactions: 1.37, theoreticalMax: 11.32 },
    { ept: 4.4, transactions: 1.34, theoreticalMax: 11.11 },
    { ept: 4.5, transactions: 1.31, theoreticalMax: 10.91 },
    { ept: 4.6, transactions: 1.28, theoreticalMax: 10.71 },
    { ept: 4.7, transactions: 1.25, theoreticalMax: 10.53 },
    { ept: 4.8, transactions: 1.23, theoreticalMax: 10.34 },
    { ept: 4.9, transactions: 1.20, theoreticalMax: 10.17 },
    { ept: 5.0, transactions: 1.18, theoreticalMax: 10.00 },
    { ept: 5.1, transactions: 1.15, theoreticalMax: 9.84 },
    { ept: 5.2, transactions: 1.13, theoreticalMax: 9.68 },
    { ept: 5.3, transactions: 1.11, theoreticalMax: 9.52 },
    { ept: 5.4, transactions: 1.09, theoreticalMax: 9.38 },
    { ept: 5.5, transactions: 1.07, theoreticalMax: 9.23 },
    { ept: 5.6, transactions: 1.05, theoreticalMax: 9.09 },
    { ept: 5.7, transactions: 1.03, theoreticalMax: 8.96 },
    { ept: 5.8, transactions: 1.01, theoreticalMax: 8.82 },
    { ept: 5.9, transactions: 1.00, theoreticalMax: 8.70 },
    { ept: 6.0, transactions: 0.98, theoreticalMax: 8.57 }
  ];

  return (
    <div className="max-w-4xl mx-auto p-8 space-y-8">
      <h1 className="text-3xl font-bold text-gray-900">
        Details and Study: Productivity Impact Analysis
      </h1>

      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <section>
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Mathematical Model</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-mono text-lg mb-4">Y = 1 / LN(x*a) * b</p>
            <p className="mt-2">Where:</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><strong>a</strong> = theoretical maximum accounting for human response time: 60 / (EPT + 1.0)</li>
              <li><strong>b</strong> = empirically fitted adjustment factor (a/1.5) that calibrates the logarithmic model to observed Doherty research data</li>
              <li><strong>x</strong> = EPT value</li>
              <li><strong>Y</strong> = Maximum transactions/minute, per Doherty Threshold</li>
            </ul>
          </div>
        </section>

        <section>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">Reference Curve</h4>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dohertyData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="ept" 
                    label={{ 
                      value: 'EPT (seconds)', 
                      position: 'bottom',
                      offset: 40
                    }}
                  />
                  <YAxis
                    label={{
                      value: 'Doherty Transactions/Minute',
                      angle: -90,
                      position: 'insideLeft'
                    }}
                  />
                  <Tooltip 
                    formatter={(value: number) => [`${value.toFixed(2)} txns/min`, 'Doherty Transactions']}
                    labelFormatter={(ept) => `EPT: ${ept}s`}
                  />
                  <Line
                    type="monotone"
                    dataKey="transactions"
                    stroke="#0176D3"
                    strokeWidth={3}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">Research Question</h4>
          <p className="text-gray-700">
            Given EPT = x, what is the maximum transactions/minute of a human working in a business 
            system, with the distractive cascade abound? The answer is derived from the actual Doherty 
            Threshold lookup table based on empirical research.
          </p>
        </section>

        <section>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">Components</h4>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>
              <strong>Logarithmic function of work:</strong>
              <p className="mt-1 text-gray-600">
                Represents the natural decay of productivity as system response time increases
              </p>
            </li>
            <li>
              <strong>Doherty Threshold Values:</strong>
              <p className="mt-1 text-gray-600">
                Empirically derived values representing actual human productivity at different EPT levels
              </p>
            </li>
            <li>
              <strong>Theoretical max work at a given EPT:</strong>
              <p className="mt-1 text-gray-600">
                The maximum possible transactions per minute achievable under ideal conditions: 60 / (EPT + 1.0), where EPT is the system response time and 1.0s represents the irreducible human cognitive processing time per transaction
              </p>
            </li>
            <li>
              <strong>Human response time constant (1.0s):</strong>
              <p className="mt-1 text-gray-600">
                Represents the baseline cognitive time required for humans to process information, make decisions, and initiate the next action, regardless of system speed
              </p>
            </li>
            <li>
              <strong>Empirical fitting parameter (1.5 divisor):</strong>
              <p className="mt-1 text-gray-600">
                A calibration constant derived by fitting the logarithmic productivity decay model to actual observed Doherty Threshold research data, capturing additional productivity losses from attention fragmentation, context switching, and mental fatigue
              </p>
            </li>
          </ul>
        </section>

        <section>
          <h5 className="text-lg font-semibold text-gray-800 mb-3">Key Insights</h5>
          <ol className="list-decimal pl-6 space-y-3 text-gray-700">
            <li>
              The relationship between EPT and productivity follows the Doherty Threshold curve based on empirical research
            </li>
            <li>
              Each role has a theoretical maximum transaction rate based on task complexity
            </li>
            <li>
              Total transaction time is the sum of system response time (EPT) and human cognitive processing time (1.0s constant), with logarithmic productivity decay capturing the "distractive cascade" effect
            </li>
            <li>
              The actual Doherty values show dramatic productivity losses as EPT increases beyond 1.0 seconds
            </li>
          </ol>
        </section>

        <section className="border-t pt-6">
          <h5 className="text-lg font-semibold text-gray-800 mb-3">Practical Applications</h5>
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700">
              This model enables organizations to:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
              <li>Quantify the impact of system performance on productivity</li>
              <li>Set evidence-based performance targets for system optimization</li>
              <li>Calculate the ROI of performance improvements</li>
              <li>Make data-driven decisions about system architecture and design</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}